<script setup lang="ts">
//=======================================================================
// Imports
//=======================================================================
import { formatDate, formatIdSource } from "@core/helpers"
import { useI18n } from "vue-i18n"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import { storeToRefs } from "pinia"
import { computed, onBeforeMount, ref } from "vue"
import {
    ComparatorAnalysis,
    NotificationEvent,
    NotificationEventTask,
} from "@types"
import { useEventBus } from "@vueuse/core"
import { ToastMessageOptions, useToast } from "primevue"
import { useConfirm } from "primevue/useconfirm"
//=======================================================================
// Store
//=======================================================================
const comparatorStore = useComparatorStore()

const {
    analyses,
    sources,
    isFetchingAllAnalysesComparatorOffers,
    isFetchingAnalysisExport,
} = storeToRefs(comparatorStore)

//=======================================================================
// I18n
//=======================================================================
const { t } = useI18n({
    messages: {
        fr: {
            historyTitle: "Historique des analyses",
            nameHeader: "Nom",
            dateHeader: "Crée le",
            idsourceHeader: "Numéro compteur",
            openLabel: "Ouvrir",
            downloadLabel: "Télécharger",
            removeLabel: "Supprimer",
            delete: {
                message: "Êtes-vous sûr de vouloir supprimer ?",
                accept: "Supprimer",
                reject: "Annuler",
            },
        },
    },
})

const getFormatedAnalyses = computed(() => {
    let index = 0

    return analyses.value
        .filter((analysis) => analysis.status === "SUCCESS")
        .map((analysis) => {
            const name = analysis.name
                ? analysis.name
                : `Comparaison des offres ${index + 1}`

            if (!analysis.name) {
                index++
            }
            return {
                _id_analysis: (analysis?._id ||
                    analysis?._id_analysis) as string,
                name,
                idsource: sources.value.find(
                    (source) =>
                        source._id_source === analysis.inputs.source._id_source
                )?.idsource as string,
                createdAt: new Date(analysis.created_at),
            }
        })
})

const visible = ref(false)

const currentAnalysisModal = ref<ComparatorAnalysis | null>(null)

const bus = useEventBus<NotificationEvent>("notification")

/**
 * Ouvre la modal de l'analyse
 * @param analysisId - Identifiant de l'analyse
 */
function onOpen(analysisId: string) {
    visible.value = true
    currentAnalysisModal.value =
        analyses.value.find(
            (analysis) =>
                (analysis._id_analysis || analysis?._id) === analysisId
        ) ?? null
    if (!currentAnalysisModal.value) {
        // On affiche une notification d'erreur
        bus.emit({
            severity: "error",
            title: "Erreur",
            message: "L'analyse n'a pas été trouvée",
        })
    }
}

const busTask = useEventBus<NotificationEventTask>("task")

const toast = useToast()
/**
 * Télécharge l'analyse
 * @param analysisId - Identifiant de l'analyse
 */
async function onDownload(analysisId: string) {
    const promise = comparatorStore.fetchCompareOffersExportByAnalyseId({
        analysisId,
        callback: (url: string) => {
            if (!url) {
                // On affiche une notification d'erreur
                bus.emit({
                    severity: "error",
                    title: "Erreur",
                    message: "Le lien de téléchargement n'a pas été trouvé",
                })
            } else {
                window.open(url, "_blank")
            }
        },
    })

    let notification: ToastMessageOptions | null = null

    function callback(_notification: ToastMessageOptions) {
        notification = _notification
    }
    // Affiche la notification de tâche en chargement
    if (isFetchingAnalysisExport.value) {
        busTask.emit({
            severity: "info",
            title: "Téléchargement en cours",
            message:
                "Le rapport est en cours de création, veuillez patienter...",
            callback,
        })
    }
    if (promise) {
        await promise
        if (notification) toast.remove(notification)
    }
}

const currentDeleteAnalysisId = ref<string | null>(null)
const confirm = useConfirm()

const confirmDelete = () => {
    confirm.require({
        header: t("delete.message"),
        acceptProps: {
            label: t("delete.accept"),
            icon: "pi pi-trash",
            severity: "danger",
        },
        rejectProps: {
            label: t("delete.reject"),
            outlined: true,
            severity: "secondary",
        },
        accept: async () => {
            await comparatorStore.deleteCompareOffersAnalysis(
                currentDeleteAnalysisId.value as string
            )
            if (!comparatorStore.hasErrorDeleteCompareOffersAnalysis) {
                bus.emit({
                    severity: "success",
                    title: "Analyse supprimée",
                    message: "L'analyse a été supprimée avec succès",
                })
            }
        },
        reject: () => {
            currentDeleteAnalysisId.value = null
        },
    })
}
/**
 * Supprime l'analyse
 * @param analysisId - Identifiant de l'analyse
 */
async function onRemove(id: string) {
    currentDeleteAnalysisId.value = id
    confirmDelete()
}
onBeforeMount(async () => {
    // Récupère toutes les analyses de comparaison d'offres
    await comparatorStore.fetchAllAnalysesComparatorOffers()
})
</script>

<template>
    <!-- History Table -->
    <BaseCard
        class="comparator-history-table"
        v-if="!isFetchingAllAnalysesComparatorOffers"
    >
        <!-- Header -->
        <template #header>
            <div
                class="p-6 gap-2.5 border-b rounded-t-lg border-gray-200 text-lg font-bold text-slate-700 bg-slate-50"
            >
                <h2>{{ t("historyTitle") }}</h2>
            </div>
        </template>
        <!-- Content -->
        <template #content>
            <div class="p-2">
                <BaseDataTable
                    :value="getFormatedAnalyses"
                    data-key="idAnalysis"
                    class="comparator-history-table"
                    paginator
                    :rows="10"
                    :total-records="getFormatedAnalyses.length"
                    :rows-per-page-options="[10, 20, 50, 100]"
                >
                    <!-- Idsource -->
                    <BaseColumn
                        field="idsource"
                        :header="t('idsourceHeader')"
                        :sortable="true"
                        :style="{ maxWidth: '8rem' }"
                    >
                        <template #body="{ data: rowData }">
                            <span class="text-slate-700 text-sm">
                                {{ formatIdSource(rowData.idsource, "pdl") }}
                            </span>
                        </template>
                    </BaseColumn>
                    <!-- Nom -->
                    <BaseColumn
                        field="name"
                        :header="t('nameHeader')"
                        :sortable="true"
                    >
                        <template #body="{ data: rowData }">
                            <span class="text-slate-700 text-sm">
                                {{ rowData.name }}
                            </span>
                        </template>
                    </BaseColumn>

                    <!-- Date -->
                    <BaseColumn
                        field="createdAt"
                        :header="t('dateHeader')"
                        :sortable="true"
                    >
                        <template #body="{ data }">
                            <span
                                class="text-slate-600 text-sm whitespace-nowrap"
                            >
                                {{
                                    formatDate(
                                        data.createdAt,
                                        "DD/MM/YYYY [à] HH:mm"
                                    )
                                }}
                            </span>
                        </template>
                    </BaseColumn>

                    <!-- Actions -->
                    <BaseColumn :sortable="false" :exportable="false">
                        <template #body="{ data }">
                            <div class="flex justify-end gap-2">
                                <BaseButton
                                    icon="pi pi-eye"
                                    variant="link"
                                    :aria-label="t('openLabel')"
                                    v-tooltip.top="t('openLabel')"
                                    @click="onOpen(data._id_analysis)"
                                />
                                <BaseButton
                                    icon="pi pi-download"
                                    variant="link"
                                    :aria-label="t('downloadLabel')"
                                    v-tooltip.top="t('downloadLabel')"
                                    @click="onDownload(data._id_analysis)"
                                />
                                <BaseButton
                                    icon="pi pi-trash"
                                    variant="link"
                                    :aria-label="t('removeLabel')"
                                    v-tooltip.top="t('removeLabel')"
                                    @click="onRemove(data._id_analysis)"
                                />
                            </div>
                        </template>
                    </BaseColumn>
                </BaseDataTable>
            </div>
            <ComparatorResultModal
                v-model:visible="visible"
                :value="currentAnalysisModal"
            />
            <BaseConfirmDialog
                :closable="false"
                position="top"
                class="max-w-[35rem] w-full"
                :pt="{
                    footer: 'justify-center!',
                    header: 'justify-center!',
                }"
            ></BaseConfirmDialog>
        </template>
    </BaseCard>
    <BaseSkeleton v-else width="100%" height="40rem" />
</template>
